/**
 * Attachment helpers — upload, list, delete.
 *
 * The signed-upload flow has three Worker hops:
 *   1. POST /api/attachments/upload-url → returns { url, key, upload_token }
 *   2. PUT  <url> with the file body (raw bytes; not authFetch — that
 *      always JSON-encodes)
 *   3. POST /api/attachments/confirm with the same token + key + metadata
 *
 * Use uploadAttachment() unless you need to drive the steps yourself.
 *
 * For displaying entity images: getEntityImageUrl(type, id) returns the
 * download URL for the most-recent image attachment, or null if none.
 * The download URL is a worker-proxied redirect that streams from R2 with
 * a 1-hour TTL — safe to use directly as <img src=...>.
 */
import { authFetch } from './auth';

export interface Attachment {
  id: string;
  ranch_id: string;
  r2_key: string;
  r2_thumb_key: string | null;
  mime_type: string;
  file_size: number;
  original_filename: string;
  linked_entity_type: string | null;
  linked_entity_id: string | null;
  description: string | null;
  uploaded_by: string | null;
  visibility: 'private' | 'internal' | 'shared';
  created_at: string;
  updated_at: string;
}

interface UploadUrlResponse {
  url: string;
  key: string;
  upload_token: string;
}

const ALLOWED_IMAGE_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
]);

const MAX_BYTES = 20 * 1024 * 1024; // 20 MB — matches Worker enforcement

/** Upload a file and link it to an entity. Returns the created Attachment. */
export async function uploadAttachment(
  file: File,
  opts: { linked_entity_type?: string; linked_entity_id?: string; description?: string } = {},
): Promise<Attachment> {
  if (file.size > MAX_BYTES) {
    throw new Error(`File exceeds 20 MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
  }

  // 1. Reserve a signed upload URL.
  const reserved = await authFetch<UploadUrlResponse>('/api/attachments/upload-url', {
    method: 'POST',
    body: JSON.stringify({
      filename: file.name,
      mime_type: file.type,
      file_size: file.size,
      linked_entity_type: opts.linked_entity_type,
      linked_entity_id: opts.linked_entity_id,
    }),
  });

  // 2. PUT the bytes directly. authFetch can't be used here because it
  // forces Content-Type: application/json and JSON.parses the response.
  const putRes = await fetch(reserved.url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => '');
    throw new Error(`Upload failed (${putRes.status}): ${text || putRes.statusText}`);
  }

  // 3. Confirm — writes D1 metadata, returns the row.
  const confirmed = await authFetch<Attachment>('/api/attachments/confirm', {
    method: 'POST',
    body: JSON.stringify({
      upload_token: reserved.upload_token,
      original_filename: file.name,
      mime_type: file.type,
      file_size: file.size,
      r2_key: reserved.key,
      linked_entity_type: opts.linked_entity_type,
      linked_entity_id: opts.linked_entity_id,
      description: opts.description,
    }),
  });
  return confirmed;
}

/** List attachments, optionally filtered by linked entity / image-only. */
export async function listAttachments(opts: {
  linked_entity_type?: string;
  linked_entity_id?: string;
  image_only?: boolean;
} = {}): Promise<Attachment[]> {
  const qs = new URLSearchParams();
  if (opts.linked_entity_type) qs.set('linked_entity_type', opts.linked_entity_type);
  if (opts.linked_entity_id) qs.set('linked_entity_id', opts.linked_entity_id);
  if (opts.image_only) qs.set('image_only', '1');
  const path = qs.toString() ? `/api/attachments?${qs}` : '/api/attachments';
  const data = await authFetch<{ items: Attachment[] }>(path);
  return data.items ?? [];
}

/** Soft-delete an attachment. */
export async function deleteAttachment(id: string): Promise<void> {
  await authFetch(`/api/attachments/${id}`, { method: 'DELETE' });
}

/** Download URL for a given attachment id. Worker-proxied, 1-hour TTL. */
export function getAttachmentDownloadUrl(id: string): string {
  return `/api/attachments/${id}/download`;
}

/** True when the MIME type is one of the supported image types. */
export function isImageMime(mime: string | null | undefined): boolean {
  return Boolean(mime && ALLOWED_IMAGE_MIMES.has(mime));
}
